import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

/**
 * Configuration for registering a namespace with a custom base URL.
 */
export interface NamespaceConfig {
  /** The namespace identifier (e.g., 'usermanagement', 'projectmanagement') */
  namespace: string;
  /** The base URL where translation files are located */
  baseUrl: string;
  /** Enable debug logging for translation loading (default: false) */
  dev?: boolean;
}

/**
 * Map to store registered namespace configurations.
 * Key: namespace, Value: baseUrl
 */
const namespaceConfigs = new Map<string, string>();

/**
 * Registers a namespace with a custom base URL for loading translations.
 * Automatically loads translations for all supported languages using HTTP fetch.
 *
 * @param config - The namespace configuration
 * @example
 * ```typescript
 * await registerNamespace({
 *   namespace: 'usermanagement',
 *   baseUrl: window.location.origin + '/usermanagement'
 * });
 * // Translations will be loaded from: {baseUrl}/locales/{lng}/translation.json
 * ```
 */
export const registerNamespace = async (config: NamespaceConfig): Promise<void> => {
  const isDev = config.dev ?? false;

  // Register the baseUrl for potential future use
  namespaceConfigs.set(config.namespace, config.baseUrl);

  // Add namespace to i18n if not already present
  if (!i18n.options.ns?.includes(config.namespace)) {
    i18n.options.ns = [...(i18n.options.ns || []), config.namespace];
  }

  // Get all supported languages
  const supportedLanguages = Array.isArray(i18n.options.supportedLngs)
    ? i18n.options.supportedLngs.filter((lng) => lng !== 'cimode')
    : ['en'];

  const sanitizedBaseUrl = config.baseUrl.replace(/\/$/, '');

  // Load translations for all supported languages in parallel
  const loadPromises = supportedLanguages.map(async (lng) => {
    const url = `${sanitizedBaseUrl}/locales/${lng}/translation.json`;
    try {
      const response = await fetch(url);

      if (!response.ok) {
        console.warn(
          `Failed to load translations for ${config.namespace}/${lng}: ${response.status} ${response.statusText}`,
        );
        return null;
      }

      const translations = await response.json();

      // Add the resource bundle to i18next
      i18n.addResourceBundle(lng, config.namespace, translations, true, true);

      if (isDev) {
        console.log(`----->>>>>   ANDRITZ: Loaded ${config.namespace}/${lng} translations`);
      }

      return { lng, success: true };
    } catch (error) {
      console.error(`Error loading translations for ${config.namespace}/${lng}:`, error);
      return { lng, success: false, error };
    }
  });

  // Wait for all translations to load
  const results = await Promise.all(loadPromises);

  if (isDev) {
    const successCount = results.filter((r) => r?.success).length;
    console.log(
      `----->>>>>   ANDRITZ: ${config.namespace} namespace registered. Loaded ${successCount}/${supportedLanguages.length} languages`,
    );
  }
};

/**
 * Initializes the i18next library with the necessary plugins and configuration.
 *
 * @see {@link https://react.i18next.com/} for more information about i18next.
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(
    {
      debug: process.env.NODE_ENV === 'development',
      partialBundledLanguages: true,
      ns: ['translation', 'ui'],
      fallbackLng: 'en',
      defaultNS: 'translation',
      load: 'currentOnly',
      supportedLngs: [
        'cs',
        'da',
        'de',
        'en',
        'es',
        'fi',
        'fr',
        'hi',
        'hu',
        'id',
        'it',
        'ja',
        'ms',
        'nl',
        'pl',
        'pt-BR',
        'pt-PT',
        'ru',
        'sv',
        'tr-TR',
        'zh',
      ],
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
        format: (value: string, format: string | undefined) => {
          if (format === 'uppercase') return value.toUpperCase();
          if (format === 'lowercase') return value.toLowerCase();
          if (format === 'capitalize') return `${value.substring(0, 1).toUpperCase()}${value.substring(1)}`;
          return value;
        },
      },
      resources: {},
      returnNull: false,
      returnEmptyString: false,
    },
    (_err, _t) => {
      console.log('----->>> ANDRITZ: i18n initialized in web-utils');
    },
  );

export default i18n;
